import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Modal, Button } from 'react-bootstrap';
import { api } from '../api';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import deLocale from 'date-fns/locale/de';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const locales = {
    'de': deLocale,
};

const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales,
});

interface EventData {
    id: number;
    title: string;
    description: string;
    location: string;
    deadline: string;
    is_public: boolean;
    voting_type?: string;
    final_option_id?: number;
    limit_food?: number;
    limit_drinks?: number;
    limit_liters?: number;
    allow_no_interest?: number;
    options: { id: number, start_time: string, end_time?: string, votes: any[], max_participants?: number }[];
    items?: { id: number, name: string, description: string, image_path: string, type: 'food'|'drink', liters?: number, max_per_user?: number }[];
    questions?: { id: number, type: 'stars'|'text', question_text: string }[];
    participants: { id: number, name: string, votes: { option_id: number, choice: string }[], comment?: string, has_no_interest?: number | boolean }[];
    current_participant?: {
        id: number;
        name: string;
        comment: string;
        has_no_interest?: number | boolean;
        votes: { option_id: number, choice: string }[];
        item_votes: { item_id: number, quantity: number }[];
        feedback_answers?: { question_id: number, value_int?: number, value_text?: string }[];
    };
}

const MarqueeIfLong = ({ text, className }: { text: string, className?: string }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const textRef = useRef<HTMLDivElement>(null);
    const [isLong, setIsLong] = useState(false);

    useEffect(() => {
        const checkOverflow = () => {
            if (containerRef.current && textRef.current) {
                const wasLong = isLong;
                if (wasLong) setIsLong(false);
                setTimeout(() => {
                    if (containerRef.current && textRef.current) {
                        setIsLong(textRef.current.scrollWidth > containerRef.current.clientWidth);
                    }
                }, 0);
            }
        };
        checkOverflow();
        window.addEventListener('resize', checkOverflow);
        return () => window.removeEventListener('resize', checkOverflow);
    }, [text, isLong]);

    return (
        <div ref={containerRef} className={`overflow-hidden ${className}`} style={{ whiteSpace: 'nowrap' }}>
            <div ref={textRef} className={isLong ? 'marquee-scroll' : 'text-truncate'}>
                {text}
                {isLong && <span style={{ paddingLeft: '2rem' }}>{text}</span>}
            </div>
        </div>
    );
};

const VotePage: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [event, setEvent] = useState<EventData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCalendar, setShowCalendar] = useState(false);
    const [showVoteModal, setShowVoteModal] = useState(false);
    const [selectedEventData, setSelectedEventData] = useState<any>(null);
    const [showErrorModal, setShowErrorModal] = useState(false);

    // My votes state: map optionId -> choice
    const [myVotes, setMyVotes] = useState<Record<number, string>>({});
    const [myNoInterest, setMyNoInterest] = useState<boolean>(false);
    // My item votes: map itemId -> quantity
    const [myItemVotes, setMyItemVotes] = useState<Record<number, number>>({});
    // My feedback votes: map questionId -> { value_int?: number, value_text?: string }
    const [myFeedbackVotes, setMyFeedbackVotes] = useState<Record<number, { value_int?: number, value_text?: string }>>({});
    
    const [myComment, setMyComment] = useState('');
    const [submitted, setSubmitted] = useState(false);

    // Join Form state
    const [joinName, setJoinName] = useState('');
    const [joinEmail, setJoinEmail] = useState('');
    const [isJoining, setIsJoining] = useState(false);
    const [timeLeft, setTimeLeft] = useState<string | null>(null);

    // Fireworks state
    const [showFireworks, setShowFireworks] = useState(false);
    
    // Image Lightbox state
    const [expandedImage, setExpandedImage] = useState<string | null>(null);

    // Flash animation states for limits
    const [flashFood, setFlashFood] = useState(false);
    const [flashDrink, setFlashDrink] = useState(false);

    // Item view mode (grid or list)
    const [itemViewMode, setItemViewMode] = useState<'grid' | 'list'>('grid');

    useEffect(() => {
        if (submitted) {
            const timer = setTimeout(() => {
                setSubmitted(false);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [submitted]);

    useEffect(() => {
        if (!event?.deadline || event.final_option_id) {
            setTimeLeft(null);
            return;
        }

        const timer = setInterval(() => {
            const now = new Date().getTime();
            const deadline = new Date(event.deadline).getTime();
            const diff = deadline - now;

            if (diff > 0 && diff < 24 * 60 * 60 * 1000) {
                const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((diff % (1000 * 60)) / 1000);
                setTimeLeft(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
            } else {
                setTimeLeft(null);
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [event?.deadline, event?.final_option_id]);

    useEffect(() => {
        const fetchEvent = async () => {
            try {
                const res = await api.get(`/vote/${token}`);
                setEvent(res.data);
                
                // Pre-fill my votes if they exist
                if (res.data.current_participant) {
                    const myData = res.data.current_participant;
                    if (myData.comment) setMyComment(myData.comment);
                    if (myData.has_no_interest === 1 || myData.has_no_interest === true) setMyNoInterest(true);
                    
                    // Time Votes
                    if (Array.isArray(myData.votes) && myData.votes.length > 0) {
                        const votesMap: Record<number, string> = {};
                        myData.votes.forEach((v: any) => {
                            votesMap[v.option_id] = v.choice;
                        });
                        setMyVotes(votesMap);
                    }

                    // Item Votes
                    if (Array.isArray(myData.item_votes) && myData.item_votes.length > 0) {
                        const iVotesMap: Record<number, number> = {};
                        myData.item_votes.forEach((v: any) => {
                            iVotesMap[v.item_id] = v.quantity;
                        });
                        setMyItemVotes(iVotesMap);
                    }

                    // Feedback Votes
                    if (Array.isArray(myData.feedback_answers) && myData.feedback_answers.length > 0) {
                        const fVotesMap: Record<number, { value_int?: number, value_text?: string }> = {};
                        myData.feedback_answers.forEach((v: any) => {
                            fVotesMap[v.question_id] = { value_int: v.value_int, value_text: v.value_text };
                        });
                        setMyFeedbackVotes(fVotesMap);
                    }
                }
            } catch (err) {
                setError('Ungültiger Link oder Event nicht gefunden.');
            } finally {
                setLoading(false);
            }
        };
        fetchEvent();

        // Live Update Polling (every 10 seconds)
        const interval = setInterval(async () => {
             try {
                const res = await api.get(`/vote/${token}`);
                setEvent(res.data);
             } catch (e) { /* ignore silent fail */ }
        }, 10000);
        return () => clearInterval(interval);
    }, [token]);

    const handleJoin = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!joinName || !joinEmail) return;
        setIsJoining(true);
        try {
            const res = await api.post(`/vote/${token}/join`, { name: joinName, email: joinEmail });
            
            if (res.data.existing) {
                alert("Diese E-Mail hat bereits abgestimmt. Ein Link zur Bearbeitung wurde an deine E-Mail-Adresse gesendet.");
                setIsJoining(false);
                return;
            }

            // Redirect to the new private link
            window.location.href = `/vote/${res.data.token}`;
        } catch (err: any) {
            alert(err.response?.data?.error || 'Fehler beim Beitreten');
            setIsJoining(false);
        }
    };

    const handleVoteChange = (optionId: number, choice: string) => {
        setMyVotes(prev => ({ ...prev, [optionId]: choice }));
    };

        const handleItemChange = (itemId: number, delta: number, type: 'food' | 'drink') => {
            if (!event) return;
    
            if (delta > 0) {
                const targetItem = event.items?.find(i => i.id === itemId);
                if (targetItem && targetItem.max_per_user && targetItem.max_per_user > 0) {
                    const currentItemQty = myItemVotes[itemId] || 0;
                    if (currentItemQty >= targetItem.max_per_user) return; // Limit reached
                }

                const limit = type === 'food' ? event.limit_food : event.limit_drinks;
                if (limit && limit > 0) {
                    // Calculate current sum for this type based on the external state
                    const itemsOfType = event.items?.filter(i => i.type === type) || [];
                    const currentSum = itemsOfType.reduce((acc, item) => acc + (myItemVotes[item.id] || 0), 0);
                    
                    if (currentSum >= limit) {
                        if (type === 'food') {
                            if (!flashFood) {
                                setFlashFood(true);
                                setTimeout(() => setFlashFood(false), 600);
                            }
                        } else {
                            if (!flashDrink) {
                                setFlashDrink(true);
                                setTimeout(() => setFlashDrink(false), 600);
                            }
                        }
                        return; // Limit reached, no increase
                    }
                }

                // Check Liter Limit
                if (type === 'drink' && event.limit_liters && event.limit_liters > 0) {
                    const drinkItems = event.items?.filter(i => i.type === 'drink') || [];
                    const currentLiters = drinkItems.reduce((acc, item) => {
                        const qty = myItemVotes[item.id] || 0;
                        return acc + (qty * (item.liters || 0));
                    }, 0);
                    const itemToAddLiters = targetItem?.liters || 0;
                    if (currentLiters + itemToAddLiters > event.limit_liters) {
                        if (!flashDrink) {
                            setFlashDrink(true);
                            setTimeout(() => setFlashDrink(false), 600);
                        }
                        return; // Limit reached, no increase
                    }
                }
            }
    
            setMyItemVotes(prev => {
                const currentQty = prev[itemId] || 0;
                const newQty = Math.max(0, currentQty + delta);
                return { ...prev, [itemId]: newQty };
            });
        };
    const handleSubmit = async () => {
        if (!event) return;

        let votesPayload: any[] = [];

        if (event.voting_type === 'order') {
            // Convert item votes map to list
            votesPayload = (event.items || []).map(item => ({
                item_id: item.id,
                quantity: myItemVotes[item.id] || 0
            }));
        } else if (event.voting_type === 'feedback') {
            // Validate that all questions are answered (or maybe optional, let's say all mandatory if stars?)
            votesPayload = (event.questions || []).map(q => {
                const ans = myFeedbackVotes[q.id] || {};
                return {
                    question_id: q.id,
                    value_int: ans.value_int,
                    value_text: ans.value_text
                };
            });
        } else {
            // Mandatory check for time options
            const missingVotes = myNoInterest ? [] : event.options.filter(opt => !myVotes[opt.id]);
            if (missingVotes.length > 0) {
                setShowErrorModal(true);
                return;
            }
            
            if (!myNoInterest) {
                votesPayload = event.options.map(opt => ({
                option_id: opt.id,
                choice: myVotes[opt.id]
            }));
            }
        }

        try {
            await api.post(`/vote/${token}`, { votes: votesPayload, has_no_interest: myNoInterest }, { params: { comment: myComment } });
            setSubmitted(true);
            setError('');
            // Reload data
            const res = await api.get(`/vote/${token}`);
            setEvent(res.data);
        } catch (err: any) {
            const msg = err.response?.data?.detail || err.response?.data?.error || 'Fehler beim Speichern.';
            setError(msg);
            alert(msg);
        }
    };

    if (loading) return <div className="text-center mt-5">Laden...</div>;
    if (error) return <div className="text-center mt-5 text-danger">{error}</div>;
    if (!event) return null;

    const isDeadlinePassed = !!event.deadline && new Date() > new Date(event.deadline);

    const formatDate = (iso: string) => {
        const d = new Date(iso);
        const base = d.toLocaleString('de-DE', { 
            weekday: 'short', day: '2-digit', month: '2-digit', 
            hour: '2-digit', minute: '2-digit'
        });
        const tz = d.toLocaleTimeString('de-DE', { timeZoneName: 'short' }).split(' ').pop();
        return `${base} Uhr (${tz})`;
    };

    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const calendarEvents = event ? event.options.map(opt => {
        const startStr = new Date(opt.start_time).toLocaleTimeString('de-DE', {hour: '2-digit', minute:'2-digit'});
        const endStr = opt.end_time 
            ? new Date(opt.end_time).toLocaleTimeString('de-DE', {hour: '2-digit', minute:'2-digit'})
            : new Date(new Date(opt.start_time).getTime() + 60*60*1000).toLocaleTimeString('de-DE', {hour: '2-digit', minute:'2-digit'});
        return {
            title: `${startStr} - ${endStr} Uhr`,
            start: new Date(opt.start_time),
            end: opt.end_time ? new Date(opt.end_time) : new Date(new Date(opt.start_time).getTime() + 60*60*1000), // Default 1h
            resource: opt
        };
    }) : [];

    const minStartTime = event && event.options.length > 0 
        ? new Date(Math.min(...event.options.map(o => new Date(o.start_time).getTime())))
        : new Date();
    // Set to start of the hour to look better
    minStartTime.setMinutes(0);
    minStartTime.setSeconds(0);

    const eventPropGetter = (e: any) => {
        const isFinal = event?.final_option_id === e.resource.id;
        
        if (isFinal) {
            return {
                style: {
                    backgroundColor: '#ffc107', // Gold/Warning for Winner
                    color: '#000',
                    border: '2px solid #000',
                    fontWeight: 'bold'
                }
            };
        }

        const myChoice = myVotes[e.resource.id];
        let backgroundColor = '#0d6efd'; // default blue
        if (myChoice === 'yes') backgroundColor = '#198754';
        else if (myChoice === 'maybe') backgroundColor = '#ffc107'; // This collides with final, maybe change? 
        // Actually, if it's final it returns early above.
        // If I voted maybe on a non-final option, it will be yellow.
        else if (myChoice === 'no') backgroundColor = '#dc3545';
        
        return { style: { backgroundColor } };
    };

    const formatHeader = (opt: any) => {
        const start = new Date(opt.start_time);
        const dateStr = start.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
        const timeStart = start.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

        if (opt.end_time) {
            const end = new Date(opt.end_time);
            const tzSuffix = new Date(opt.start_time).toLocaleTimeString('de-DE', { timeZoneName: 'short' }).split(' ').pop();
            // Check if same day
            if (start.toDateString() === end.toDateString()) {
                 const timeEnd = end.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
                 return (
                     <>
                        <div>{dateStr}</div>
                        <div className="fw-normal">{timeStart} - {timeEnd} <small className="text-muted">Uhr ({tzSuffix})</small></div>
                     </>
                 );
            }
            // Different days
             return (
                 <>
                    <div>{dateStr} {timeStart}</div>
                    <div>-</div>
                    <div>{formatDate(opt.end_time)}</div>
                 </>
             );
        }
        
        // No end time
        const tzSuffix = new Date(opt.start_time).toLocaleTimeString('de-DE', { timeZoneName: 'short' }).split(' ').pop();
        return (
             <>
                <div>{dateStr}</div>
                <div className="fw-normal">{timeStart} <small className="text-muted">Uhr ({tzSuffix})</small></div>
             </>
        );
    };

    // Calculate current sums for Sticky Bar
    const currentFoodSum = event?.items?.filter(i => i.type === 'food').reduce((acc, i) => acc + (myItemVotes[i.id] || 0), 0) || 0;
    const currentDrinkSum = event?.items?.filter(i => i.type === 'drink').reduce((acc, i) => acc + (myItemVotes[i.id] || 0), 0) || 0;
    const currentLiters = event?.items?.filter(i => i.type === 'drink').reduce((acc, i) => acc + ((myItemVotes[i.id] || 0) * (i.liters || 0)), 0) || 0;

    return (
        <div className="main-container mt-5 mb-5 pb-5">
            {showFireworks && <FireworksCanvas />}
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row align-items-center mb-3">
                        <div className="col-12 col-md-8 col-lg-9 order-2 order-md-1 text-center text-md-start">
                            <h1 className="card-title display-4 mb-0">{event.title}</h1>
                        </div>
                        {event.id && (
                            <div className="col-12 col-md-4 col-lg-3 order-1 order-md-2 text-center text-md-end mb-3 mb-md-0">
                                <img 
                                    src={`${api.defaults.baseURL}/events/${event.id}/logo`} 
                                    alt="" 
                                    style={{ maxHeight: '120px', maxWidth: '100%', objectFit: 'contain' }}
                                    onError={(e) => (e.currentTarget.style.display = 'none')}
                                />
                            </div>
                        )}
                    </div>
                    {event.description && <p className="text-muted fs-5 text-center text-md-start mb-4" style={{ whiteSpace: 'pre-wrap' }}>{event.description}</p>}
                    <div className="row text-secondary mt-3 fs-5">
                        <div className="col-md-3">
                            {(event as any).current_participant?.name ? (
                                <div>👤 {((event as any).current_participant.name)}</div>
                            ) : (
                                <div>👤 Gast</div>
                            )}
                        </div>
                        <div className="col-md-3">
                            📍 {event.location ? (
                                <>
                                    {event.location} <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`} target="_blank" rel="noopener noreferrer" className="ms-1">(Auf Karte ansehen)</a>
                                </>
                            ) : (
                                'Kein Ort'
                            )}
                        </div>
                        <div className="col-md-3">
                            ⏰ Rückmeldefrist: {event.deadline ? formatDate(event.deadline) : 'Keine'}
                            {timeLeft && (
                                <div className="text-danger fw-bold animate-pulse mt-1">
                                    ⏳ {timeLeft}
                                </div>
                            )}
                        </div>
                        <div className="col-md-3 text-md-end">
                            <span className="badge bg-light text-dark border">
                                🕒 Uhr ({userTimezone})
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {event.final_option_id && (
                <div className="alert alert-info shadow-sm d-flex justify-content-between align-items-center fs-5">
                    <div>
                        <h4 className="alert-heading">🎉 Termin fixiert!</h4>
                        <p className="mb-0">Es wurde folgender Termin ausgewählt: <strong>{
                            formatDate(event.options.find(o => o.id === event.final_option_id)?.start_time || '')
                        }</strong></p>
                    </div>
                    <a href={`${window.location.origin}/api/events/${event.id}/ics`} className="btn btn-primary btn-lg">
                        📅 Kalender-Eintrag (.ics)
                    </a>
                </div>
            )}

            {!(event as any).current_participant && !event.final_option_id && (
                <div className="card border-primary shadow mb-4">
                    <div className="card-header bg-primary text-white">
                        <h4 className="mb-0">Teilnahme bestätigen</h4>
                    </div>
                    <div className="card-body fs-5">
                        <p>Bitte gib deinen Namen und deine E-Mail-Adresse an, um abzustimmen.</p>
                        <form onSubmit={handleJoin} className="row g-3">
                            <div className="col-md-5">
                                <input type="text" className="form-control form-control-lg" placeholder="Name" value={joinName} onChange={e => setJoinName(e.target.value)} required />
                            </div>
                            <div className="col-md-5">
                                <input type="email" className="form-control form-control-lg" placeholder="E-Mail-Adresse" value={joinEmail} onChange={e => setJoinEmail(e.target.value)} required />
                            </div>
                            <div className="col-md-2">
                                <button type="submit" className="btn btn-primary btn-lg w-100" disabled={isJoining}>
                                    {isJoining ? 'Lädt...' : 'Bestätigen'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}



            {((event as any).current_participant || event.final_option_id) && (
                <>
                    {event.voting_type === 'order' ? (
                        <div className="row g-4">
                            <div className="col-12">
                                <div className="d-flex justify-content-end mb-4">
                                    <div className="btn-group btn-group-sm">
                                        <button className={`btn ${itemViewMode === 'grid' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setItemViewMode('grid')} title="Rasteransicht">
                                            ⊞
                                        </button>
                                        <button className={`btn ${itemViewMode === 'list' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setItemViewMode('list')} title="Listenansicht">
                                            ☰
                                        </button>
                                    </div>
                                </div>
                                {event.items && event.items.length > 0 ? (
                                    <div className={`row ${itemViewMode === 'grid' ? 'row-cols-2 row-cols-md-3 row-cols-lg-4 row-cols-xl-5' : 'row-cols-1'} g-3`}>
                                        {event.items.map(item => {
                                            const isLimitReached = (item.max_per_user && item.max_per_user > 0 && (myItemVotes[item.id] || 0) >= item.max_per_user) ? true :
                                                                   item.type === 'food' && event.limit_food && event.limit_food > 0 ? currentFoodSum >= event.limit_food :
                                                                   item.type === 'drink' && event.limit_drinks && event.limit_drinks > 0 ? currentDrinkSum >= event.limit_drinks :
                                                                   item.type === 'drink' && event.limit_liters && event.limit_liters > 0 ? (currentLiters + (item.liters || 0)) > event.limit_liters : false;
                                            return (
                                            <div key={item.id} className="col">
                                                <div className={`card shadow-sm border-0 ${itemViewMode === 'list' ? 'flex-row align-items-center p-2' : 'h-100'}`}>
                                                    <div style={itemViewMode === 'grid' ? { position: 'relative', paddingTop: '75%' } : { width: '80px', height: '80px', position: 'relative', flexShrink: 0, borderRadius: '8px', overflow: 'hidden' }}>
                                                        {item.image_path ? (
                                                            <img 
                                                                src={`${window.location.origin}${item.image_path}`} 
                                                                alt={item.name} 
                                                                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'contain', cursor: 'zoom-in' }} 
                                                                onClick={() => setExpandedImage(`${window.location.origin}${item.image_path}`)}
                                                            />
                                                        ) : (
                                                            <div className="bg-light text-muted d-flex align-items-center justify-content-center" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
                                                                Kein Bild
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className={`card-body d-flex ${itemViewMode === 'list' ? 'flex-row align-items-center justify-content-between p-2 flex-grow-1' : 'flex-column p-2'}`} style={itemViewMode === 'list' ? { width: 'calc(100% - 80px)' } : {}}>
                                                        <div className={itemViewMode === 'list' ? 'flex-grow-1 overflow-hidden pe-3' : 'mb-auto'}>
                                                            <MarqueeIfLong text={item.name} className="card-title fw-bold mb-1 fs-6" />
                                                            <p className={`card-text text-muted small mb-2`} title={item.description} style={{ whiteSpace: 'pre-wrap' }}>{item.description}</p>
                                                            {itemViewMode === 'list' && (
                                                                <div className="d-flex gap-1 flex-wrap mt-1">
                                                                    <span className={`badge ${item.type === 'food' ? 'bg-warning' : 'bg-info'} text-dark uppercase`} style={{fontSize: '0.6rem'}}>
                                                                        {item.type === 'food' ? 'Essen' : 'Trinken'}
                                                                    </span>
                                                                    {(item.liters || 0) > 0 ? (
                                                                        <span className="badge bg-secondary" style={{fontSize: '0.6rem'}}>{item.liters} L</span>
                                                                    ) : null}
                                                                    {(item.max_per_user || 0) > 0 ? (
                                                                        <span className="badge bg-danger" style={{fontSize: '0.6rem'}}>Max: {item.max_per_user}</span>
                                                                    ) : null}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className={`d-flex ${itemViewMode === 'list' ? 'align-items-center flex-shrink-0' : 'justify-content-between align-items-center mt-2'}`}>
                                                            {itemViewMode === 'grid' && (
                                                                <div className="d-flex flex-column gap-1 align-items-start">
                                                                    <span className={`badge ${item.type === 'food' ? 'bg-warning' : 'bg-info'} text-dark uppercase`} style={{fontSize: '0.6rem'}}>
                                                                        {item.type === 'food' ? 'Essen' : 'Trinken'}
                                                                    </span>
                                                                    {(item.liters || 0) > 0 ? (
                                                                        <span className="badge bg-secondary" style={{fontSize: '0.6rem'}}>{item.liters} L</span>
                                                                    ) : null}
                                                                    {(item.max_per_user || 0) > 0 ? (
                                                                        <span className="badge bg-danger" style={{fontSize: '0.6rem'}}>Max: {item.max_per_user}</span>
                                                                    ) : null}
                                                                </div>
                                                            )}
                                                            <div className="d-flex align-items-center gap-2">
                                                                <button 
                                                                    className="btn btn-sm btn-outline-secondary rounded-circle p-0 d-flex align-items-center justify-content-center" 
                                                                    style={{width:'32px', height:'32px', fontSize: '1.2rem', lineHeight: 0}} 
                                                                    onClick={() => handleItemChange(item.id, -1, item.type)}
                                                                    disabled={isDeadlinePassed}
                                                                ><span style={{marginTop: '-2px'}}>-</span></button>
                                                                <span className="fw-bold text-center" style={{minWidth: '40px', fontSize: '1.5rem'}}>
                                                                    {myItemVotes[item.id] || 0}
                                                                </span>
                                                                <button 
                                                                    className={`btn btn-sm ${isLimitReached ? 'btn-secondary opacity-50' : 'btn-primary'} rounded-circle p-0 d-flex align-items-center justify-content-center`} 
                                                                    style={{width:'32px', height:'32px', fontSize: '1.2rem', lineHeight: 0, transition: 'all 0.2s'}} 
                                                                    onClick={() => handleItemChange(item.id, 1, item.type)}
                                                                    disabled={isDeadlinePassed}
                                                                ><span style={{marginTop: '-2px'}}>+</span></button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )})}
                                    </div>
                                ) : (
                                    <div className="alert alert-info">Noch keine Artikel zur Auswahl verfügbar.</div>
                                )}
                            </div>
                        </div>
                    ) : event.voting_type === 'feedback' ? (
                        <div className="row g-4 mt-2">
                            <div className="col-12 col-md-8 mx-auto">
                                <h4 className="mb-4">Bitte gib dein Feedback ab</h4>
                                {event.questions && event.questions.map((q) => (
                                    <div key={q.id} className="card mb-3 shadow-sm">
                                        <div className="card-body">
                                            <h6 className="card-title fw-bold mb-3">{q.question_text}</h6>
                                            {q.type === 'stars' ? (
                                                <div className="d-flex gap-2" style={{ fontSize: '2rem', cursor: 'pointer' }}>
                                                    {[1, 2, 3, 4, 5].map(star => (
                                                        <span 
                                                            key={star} 
                                                            onClick={() => setMyFeedbackVotes(prev => ({ ...prev, [q.id]: { ...prev[q.id], value_int: star } }))}
                                                            style={{ color: (myFeedbackVotes[q.id]?.value_int || 0) >= star ? '#ffc107' : '#e4e5e9', transition: 'color 0.2s' }}
                                                        >
                                                            ★
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : (
                                                <textarea 
                                                    className="form-control" 
                                                    rows={3} 
                                                    placeholder="Deine Antwort..."
                                                    value={myFeedbackVotes[q.id]?.value_text || ''}
                                                    onChange={e => setMyFeedbackVotes(prev => ({ ...prev, [q.id]: { ...prev[q.id], value_text: e.target.value } }))}
                                                />
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <>
                    <div className="d-flex justify-content-end mb-3">
                        <div className="btn-group">
                            <button className={`btn btn-lg ${!showCalendar ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setShowCalendar(false)}>📋 Liste</button>
                            <button className={`btn btn-lg ${showCalendar ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setShowCalendar(true)}>📅 Kalender</button>
                        </div>
                    </div>

                    {showCalendar ? (
                        <div style={{ height: '800px' }} className="mb-4">
                            <Calendar
                                localizer={localizer}
                                events={calendarEvents}
                                startAccessor="start"
                                endAccessor="end"
                                culture="de"
                                scrollToTime={minStartTime}
                                messages={{
                                    next: "Weiter",
                                    previous: "Zurück",
                                    today: "Heute",
                                    month: "Monat",
                                    week: "Woche",
                                    day: "Tag",
                                    date: "Datum",
                                    time: "Uhrzeit",
                                    event: "Termin",
                                    noEventsInRange: "Keine Termine in diesem Zeitraum."
                                }}
                                eventPropGetter={eventPropGetter}
                                onSelectEvent={(e) => {
                                    setSelectedEventData(e.resource);
                                    setShowVoteModal(true);
                                }}
                                components={{
                                    agenda: {
                                        event: AgendaEvent
                                    }
                                }}
                                views={['month', 'week', 'day', 'agenda']}
                            />
                            <p className="text-muted text-center small mt-2">Klicken Sie auf einen Termin, um abzustimmen.</p>
                        </div>
                    ) : (
                    <>
                    {/* Mobile View for 'Your Vote' */}
                                        {event.allow_no_interest === 1 && !event.final_option_id && (
                        <div className="alert alert-secondary d-flex justify-content-between align-items-center mb-4 border border-secondary shadow-sm">
                            <div>
                                <h5 className="alert-heading mb-1">Kein Interesse?</h5>
                                <p className="mb-0 small">Für dieses Thema besteht kein Interesse.</p>
                            </div>
                            <div className="form-check form-switch fs-4">
                                <input 
                                    className="form-check-input" 
                                    type="checkbox" 
                                    checked={myNoInterest} 
                                    onChange={e => setMyNoInterest(e.target.checked)} 
                                    disabled={isDeadlinePassed}
                                    style={{cursor: 'pointer'}}
                                />
                            </div>
                        </div>
                    )}
<div className="d-block d-lg-none mb-4 fs-5">
                        <h5 className="mb-3">Deine Abstimmung</h5>
                        {event.options.map(opt => {
                            if (myNoInterest) return null;
                            const yesVotes = event.participants.filter(p => p.votes.find(v => v.option_id === opt.id && v.choice === 'yes')).length;
                            const isFull = (opt as any).max_participants > 0 && yesVotes >= (opt as any).max_participants;
                            const myChoice = myVotes[opt.id];
                            const isFinal = opt.id === event.final_option_id;
                            
                            return (
                                <div key={opt.id} className={`d-flex align-items-center justify-content-between mb-3 border-bottom pb-2 ${isFinal ? 'bg-success-subtle p-2 rounded border border-success' : ''}`}>
                                    <div className="me-2 small">
                                        {formatHeader(opt)}
                                        {isFinal && <div className="text-success fw-bold">🎉 Termin fixiert</div>}
                                        {(opt as any).max_participants > 0 && (
                                            <div className="text-muted mt-1" style={{fontSize: '0.8rem'}}>
                                                {yesVotes} / {(opt as any).max_participants} belegt
                                            </div>
                                        )}
                                    </div>
                                    <div className="d-flex gap-1">
                                        {event.voting_type === 'priority' ? (
                                            <>
                                                <button className={`btn btn-sm ${myChoice === 'yes' ? 'btn-success' : 'btn-outline-success'}`} onClick={() => handleVoteChange(opt.id, 'yes')} disabled={(isFull && myChoice !== 'yes') || !!event.final_option_id || isDeadlinePassed}>
                                                    {isFull && myChoice !== 'yes' ? 'Voll' : '1. Wahl'}
                                                </button>
                                                <button className={`btn btn-sm ${myChoice === 'maybe' ? 'btn-warning' : 'btn-outline-warning'}`} onClick={() => handleVoteChange(opt.id, 'maybe')} disabled={!!event.final_option_id || isDeadlinePassed}>
                                                    2. Wahl
                                                </button>
                                                <button className={`btn btn-sm ${myChoice === 'no' ? 'btn-danger' : 'btn-outline-danger'}`} onClick={() => handleVoteChange(opt.id, 'no')} disabled={!!event.final_option_id || isDeadlinePassed}>
                                                    Nein
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button className={`btn btn-sm ${myChoice === 'yes' ? 'btn-success' : 'btn-outline-success'}`} onClick={() => handleVoteChange(opt.id, 'yes')} disabled={(isFull && myChoice !== 'yes') || !!event.final_option_id || isDeadlinePassed}>
                                                    {isFull && myChoice !== 'yes' ? 'Voll' : 'Ja'}
                                                </button>
                                                <button className={`btn btn-sm ${myChoice === 'maybe' ? 'btn-warning' : 'btn-outline-warning'}`} onClick={() => handleVoteChange(opt.id, 'maybe')} disabled={!!event.final_option_id || isDeadlinePassed}>
                                                    Vielleicht
                                                </button>
                                                <button className={`btn btn-sm ${myChoice === 'no' ? 'btn-danger' : 'btn-outline-danger'}`} onClick={() => handleVoteChange(opt.id, 'no')} disabled={!!event.final_option_id || isDeadlinePassed}>
                                                    Nein
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className={`table-responsive ${!event.is_public ? 'd-none d-lg-block' : ''}`}>
                        <table className="table text-center align-middle fs-5">
                            <thead className="table-light">
                                <tr>
                                    <th style={{ minWidth: '150px' }}>Teilnehmer</th>
                                    {event.options.map(opt => (
                                        <th key={opt.id} style={{ minWidth: '100px' }} className={opt.id === event.final_option_id ? 'bg-success-subtle border-success' : ''}>
                                            {formatHeader(opt)}
                                            {opt.id === event.final_option_id && <div className="text-success fw-bold small mt-1">🎉 Fixiert</div>}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {/* User Input Row */}
                                {!myNoInterest && (
                                <tr className="table-primary border-primary d-none d-lg-table-row">
                                    <td className="align-middle fw-bold text-start">
                                        Deine Abstimmung
                                    </td>
                                    {event.options.map(opt => {
                                        // Calculate occupancy
                                        const yesVotes = event.participants.filter(p => p.votes.find(v => v.option_id === opt.id && v.choice === 'yes')).length;
                                        const isFull = (opt as any).max_participants > 0 && yesVotes >= (opt as any).max_participants;
                                        const myChoice = myVotes[opt.id];
                                        const isFinal = opt.id === event.final_option_id;

                                        return (
                                            <td key={opt.id} className={`align-middle ${isFinal ? 'bg-success-subtle border-success' : ''}`}>
                                                <div className="btn-group-vertical w-100">
                                                    {event.voting_type === 'priority' ? (
                                                        <>
                                                            <button className={`btn btn-sm ${myChoice === 'yes' ? 'btn-success' : 'btn-outline-success'}`} onClick={() => handleVoteChange(opt.id, 'yes')} disabled={(isFull && myChoice !== 'yes') || !!event.final_option_id || isDeadlinePassed}>
                                                                {isFull && myChoice !== 'yes' ? 'Voll' : '1. Wahl'}
                                                            </button>
                                                            <button className={`btn btn-sm ${myChoice === 'maybe' ? 'btn-warning' : 'btn-outline-warning'}`} onClick={() => handleVoteChange(opt.id, 'maybe')} disabled={!!event.final_option_id || isDeadlinePassed}>
                                                                2. Wahl
                                                            </button>
                                                            <button className={`btn btn-sm ${myChoice === 'no' ? 'btn-danger' : 'btn-outline-danger'}`} onClick={() => handleVoteChange(opt.id, 'no')} disabled={!!event.final_option_id || isDeadlinePassed}>
                                                                Nein
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <button className={`btn btn-sm ${myChoice === 'yes' ? 'btn-success' : 'btn-outline-success'}`} onClick={() => handleVoteChange(opt.id, 'yes')} disabled={(isFull && myChoice !== 'yes') || !!event.final_option_id || isDeadlinePassed}>
                                                                {isFull && myChoice !== 'yes' ? 'Voll' : 'Ja'}
                                                            </button>
                                                            <button className={`btn btn-sm ${myChoice === 'maybe' ? 'btn-warning' : 'btn-outline-warning'}`} onClick={() => handleVoteChange(opt.id, 'maybe')} disabled={!!event.final_option_id || isDeadlinePassed}>
                                                                Vielleicht
                                                            </button>
                                                            <button className={`btn btn-sm ${myChoice === 'no' ? 'btn-danger' : 'btn-outline-danger'}`} onClick={() => handleVoteChange(opt.id, 'no')} disabled={!!event.final_option_id || isDeadlinePassed}>
                                                                Nein
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                                {(opt as any).max_participants > 0 && (
                                                    <div className="small mt-1 text-muted" style={{fontSize: '0.8rem'}}>
                                                        {yesVotes} / {(opt as any).max_participants} belegt
                                                    </div>
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>

                                )}
                                {/* Other Participants */}
                                {event.is_public && event.participants.map(p => (
                                    <tr key={p.id}>
                                        <td className="text-start fw-bold">{p.name} <br/> <small className="fw-normal text-muted">{p.comment}</small></td>
                                        {p.has_no_interest ? (
                                            <td colSpan={event.options.length} className="text-danger fw-bold bg-danger-subtle align-middle fs-6">
                                                Kein Interesse am Thema
                                            </td>
                                        ) : (
                                            event.options.map(opt => {
                                                const vote = p.votes.find(v => v.option_id === opt.id);
                                            let icon = '❔';
                                            let color = 'text-muted';
                                            const isFinal = opt.id === event.final_option_id;
                                            
                                            if (event.voting_type === 'priority') {
                                                if (vote?.choice === 'yes') { icon = '🥇'; color = 'text-success'; }
                                                if (vote?.choice === 'no') { icon = '❌'; color = 'text-danger'; }
                                                if (vote?.choice === 'maybe') { icon = '🥈'; color = 'text-warning'; }
                                            } else {
                                                if (vote?.choice === 'yes') { icon = '✅'; color = 'text-success'; }
                                                if (vote?.choice === 'no') { icon = '❌'; color = 'text-danger'; }
                                                if (vote?.choice === 'maybe') { icon = '⚠️'; color = 'text-warning'; }
                                            }
                                            
                                            return <td key={opt.id} className={`align-middle fs-3 ${color} ${isFinal ? 'bg-success-subtle border-success' : ''}`}>{icon}</td>;
                                            })
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    </>
                    )}
                    </>
                    )}

                    {event.voting_type === 'order' && (event as any).current_participant && (
                        <div className="mt-5 d-flex flex-column align-items-center justify-content-center gap-3 mobile-sticky-bottom">
                            <h6 className="mb-0 fw-bold text-muted text-uppercase tracking-wider" style={{ fontSize: '0.85rem' }}>Deine Auswahl</h6>
                            <div className="order-status-card d-flex justify-content-center gap-2 gap-md-4 px-2 px-md-4 py-2 align-items-center w-100" style={{ maxWidth: '450px' }}>
                                <div className={`order-status-item flex-grow-1 ${flashFood ? 'flash-warning rounded' : ''}`} style={{ transition: 'background-color 0.1s', padding: '0.25rem' }}>
                                    <span className="label text-uppercase tracking-wider" style={{fontSize: '0.65rem'}}>Essen</span>
                                    <span className={`value ${currentFoodSum >= (event.limit_food || 999) ? 'text-warning' : 'text-primary'}`} style={{ fontSize: '1.25rem' }}>
                                        {currentFoodSum} <small className="text-muted" style={{fontSize: '0.8rem'}}>/ {event.limit_food || '∞'}</small>
                                    </span>
                                </div>
                                <div className="vr h-100 mx-1 d-none d-sm-block" style={{opacity: 0.2, width: '1px', backgroundColor: 'currentColor'}}></div>
                                <div className={`order-status-item flex-grow-1 ${flashDrink ? 'flash-warning rounded' : ''}`} style={{ transition: 'background-color 0.1s', padding: '0.25rem' }}>
                                    <span className="label text-uppercase tracking-wider" style={{fontSize: '0.65rem'}}>Getränke</span>
                                    <span className={`value ${currentDrinkSum >= (event.limit_drinks || 999) ? 'text-warning' : 'text-primary'}`} style={{ fontSize: '1.25rem' }}>
                                        {currentDrinkSum} <small className="text-muted" style={{fontSize: '0.8rem'}}>/ {event.limit_drinks || '∞'} Stk.</small>
                                    </span>
                                    {(event.limit_liters || 0) > 0 ? (
                                        <div className={`value ${currentLiters >= (event.limit_liters || 0) ? 'text-warning' : 'text-primary'}`} style={{ fontSize: '1rem', marginTop: '-5px' }}>
                                            {currentLiters.toFixed(1)} <small className="text-muted" style={{fontSize: '0.8rem'}}>/ {event.limit_liters} L</small>
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="mt-4">
                        <label className="form-label fs-5">Kommentar (Optional)</label>
                        <input
                            type="text"
                            className="form-control form-control-lg"
                            placeholder="Allergien, Sonderwünsche..."
                            value={myComment}
                            onChange={e => setMyComment(e.target.value)}
                            disabled={!!event.final_option_id || isDeadlinePassed}
                        />
                    </div>

                    {submitted && <div className="alert alert-success mt-3">Vielen Dank! Deine Auswahl wurde gespeichert.</div>}

                    <div className="mt-4 text-end">
                        <button
                            className="btn btn-primary btn-lg px-5"
                            onClick={handleSubmit}
                            disabled={!!event.final_option_id || !(event as any).current_participant || isDeadlinePassed}
                        >
                            Abstimmung speichern
                        </button>
                    </div>

                    <Modal show={showVoteModal} onHide={() => setShowVoteModal(false)} centered>
                        <Modal.Header closeButton>
                            <Modal.Title>Termin Abstimmen</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {selectedEventData && (
                                <div className="text-center">
                                    <div className="mb-4 lead">
                                        {formatHeader(selectedEventData)}
                                    </div>
                                    <div className="d-grid gap-2">
                                        {(() => {
                                            const opt = selectedEventData;
                                            const yesVotes = event.participants.filter(p => p.votes.find(v => v.option_id === opt.id && v.choice === 'yes')).length;
                                            const isFull = (opt as any).max_participants > 0 && yesVotes >= (opt as any).max_participants;
                                            const myChoice = myVotes[opt.id];

                                            if (event.voting_type === 'priority') {
                                                return (
                                                    <>
                                                        <Button size="lg" variant={myChoice === 'yes' ? 'success' : 'outline-success'} onClick={() => { handleVoteChange(opt.id, 'yes'); setShowVoteModal(false); }} disabled={(isFull && myChoice !== 'yes') || !!event.final_option_id || isDeadlinePassed}>
                                                            {isFull && myChoice !== 'yes' ? 'Voll' : '1. Wahl'}
                                                        </Button>
                                                        <Button size="lg" variant={myChoice === 'maybe' ? 'warning' : 'outline-warning'} onClick={() => { handleVoteChange(opt.id, 'maybe'); setShowVoteModal(false); }} disabled={!!event.final_option_id || isDeadlinePassed}>
                                                            2. Wahl
                                                        </Button>
                                                        <Button size="lg" variant={myChoice === 'no' ? 'danger' : 'outline-danger'} onClick={() => { handleVoteChange(opt.id, 'no'); setShowVoteModal(false); }} disabled={!!event.final_option_id || isDeadlinePassed}>
                                                            Nein
                                                        </Button>
                                                    </>
                                                );
                                            } else {
                                                return (
                                                    <>
                                                        <Button size="lg" variant={myChoice === 'yes' ? 'success' : 'outline-success'} onClick={() => { handleVoteChange(opt.id, 'yes'); setShowVoteModal(false); }} disabled={(isFull && myChoice !== 'yes') || !!event.final_option_id || isDeadlinePassed}>
                                                            {isFull && myChoice !== 'yes' ? 'Voll' : 'Ja'}
                                                        </Button>
                                                        <Button size="lg" variant={myChoice === 'maybe' ? 'warning' : 'outline-warning'} onClick={() => { handleVoteChange(opt.id, 'maybe'); setShowVoteModal(false); }} disabled={!!event.final_option_id || isDeadlinePassed}>
                                                            Vielleicht
                                                        </Button>
                                                        <Button size="lg" variant={myChoice === 'no' ? 'danger' : 'outline-danger'} onClick={() => { handleVoteChange(opt.id, 'no'); setShowVoteModal(false); }} disabled={!!event.final_option_id || isDeadlinePassed}>
                                                            Nein
                                                        </Button>
                                                    </>
                                                );
                                            }
                                        })()}
                                    </div>
                                </div>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                             <Button variant="secondary" onClick={() => setShowVoteModal(false)}>Schließen</Button>
                        </Modal.Footer>
                    </Modal>

                    <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} centered>
                        <Modal.Header closeButton className="bg-danger text-white">
                            <Modal.Title>Unvollständig</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <p className="lead">Bitte stimme für alle Terminvorschläge ab.</p>
                            <p>Es sind noch nicht alle Optionen bewertet worden.</p>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowErrorModal(false)}>Verstanden</Button>
                        </Modal.Footer>
                    </Modal>

                    <Modal show={!!expandedImage} onHide={() => setExpandedImage(null)} centered size="lg">
                        <Modal.Body className="p-0 text-center bg-dark">
                            {expandedImage && (
                                <img 
                                    src={expandedImage} 
                                    alt="Detail" 
                                    style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }} 
                                />
                            )}
                        </Modal.Body>
                        <Modal.Footer className="bg-dark border-0 justify-content-center">
                             <Button variant="outline-light" onClick={() => setExpandedImage(null)}>Schließen</Button>
                        </Modal.Footer>
                    </Modal>

                    {/* Rocket Button */}
                    {(event.final_option_id || isDeadlinePassed) && (
                        <button
                            className="btn position-fixed bottom-0 start-0 m-4 rounded-circle shadow-lg d-flex align-items-center justify-content-center"
                            style={{
                                width: '50px',
                                height: '50px',
                                zIndex: 1050,
                                backgroundColor: 'var(--bs-body-color)',
                                color: 'var(--bs-body-bg)',
                                border: 'none',
                                fontSize: '1.2rem'
                            }}
                            onClick={() => setShowFireworks(true)}
                            title="Feiern!"
                        >
                            🚀
                        </button>
                    )}
                </>
            )}
        </div>
    );
};

// Custom Agenda Event Component
const AgendaEvent = ({ event }: any) => {
    return (
        <div className="d-flex justify-content-between align-items-center w-100 p-1">
            <span className="fw-bold">{event.title}</span>
        </div>
    );
};

const FireworksCanvas: React.FC = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        let w = canvas.width = window.innerWidth;
        let h = canvas.height = window.innerHeight;

        const resize = () => {
            w = canvas.width = window.innerWidth;
            h = canvas.height = window.innerHeight;
        };
        window.addEventListener('resize', resize);

        // --- Physics & Config ---
        const GRAVITY = 0.04;
        const FRICTION = 0.96; 
        const PARTICLE_COUNT = 80;
        
        // --- Helpers ---
        const random = (min: number, max: number) => Math.random() * (max - min) + min;
        const calculateDistance = (p1x: number, p1y: number, p2x: number, p2y: number) => {
            const xDistance = p1x - p2x;
            const yDistance = p1y - p2y;
            return Math.sqrt(Math.pow(xDistance, 2) + Math.pow(yDistance, 2));
        };

        // --- Classes ---
        class Firework {
            x: number;
            y: number;
            sx: number;
            sy: number;
            tx: number;
            ty: number;
            distanceToTarget: number;
            distanceTraveled: number;
            coordinates: [number, number][];
            angle: number;
            speed: number;
            acceleration: number;
            brightness: number;
            hue: number;

            constructor(sx: number, sy: number, tx: number, ty: number) {
                this.x = sx;
                this.y = sy;
                this.sx = sx;
                this.sy = sy;
                this.tx = tx;
                this.ty = ty;
                this.distanceToTarget = calculateDistance(sx, sy, tx, ty);
                this.distanceTraveled = 0;
                this.coordinates = [];
                // Track past coordinates for trail effect
                for (let i = 0; i < 3; i++) {
                    this.coordinates.push([sx, sy]);
                }
                this.angle = Math.atan2(ty - sy, tx - sx);
                this.speed = 2;
                this.acceleration = 1.02;
                this.brightness = random(50, 70);
                this.hue = random(0, 360);
            }

            update(index: number) {
                this.coordinates.pop();
                this.coordinates.unshift([this.x, this.y]);

                // Speed up as it launches
                if (this.speed < 8) {
                     this.speed *= this.acceleration;
                }
                
                const vx = Math.cos(this.angle) * this.speed;
                const vy = Math.sin(this.angle) * this.speed;
                
                this.distanceTraveled = calculateDistance(this.sx, this.sy, this.x + vx, this.y + vy);

                // Check if reached target
                if (this.distanceTraveled >= this.distanceToTarget) {
                    createParticles(this.tx, this.ty, this.hue);
                    fireworks.splice(index, 1);
                } else {
                    this.x += vx;
                    this.y += vy;
                }
            }

            draw() {
                ctx!.beginPath();
                ctx!.moveTo(this.coordinates[this.coordinates.length - 1][0], this.coordinates[this.coordinates.length - 1][1]);
                ctx!.lineTo(this.x, this.y);
                ctx!.strokeStyle = `hsl(${this.hue}, 100%, ${this.brightness}%)`;
                ctx!.lineWidth = 2;
                ctx!.stroke();
            }
        }

        class Particle {
            x: number;
            y: number;
            vx: number;
            vy: number;
            coordinates: [number, number][];
            alpha: number;
            decay: number;
            hue: number;
            brightness: number;

            constructor(x: number, y: number, hue: number) {
                this.x = x;
                this.y = y;
                this.coordinates = [];
                for (let i = 0; i < 5; i++) {
                    this.coordinates.push([x, y]);
                }
                
                // Random angle and speed
                const angle = random(0, Math.PI * 2);
                const speed = random(1, 5);
                
                this.vx = Math.cos(angle) * speed;
                this.vy = Math.sin(angle) * speed;
                
                this.alpha = 1;
                this.decay = random(0.007, 0.015);
                this.hue = hue + random(-20, 20); // Slight color variation
                this.brightness = random(50, 80);
            }

            update(index: number) {
                this.coordinates.pop();
                this.coordinates.unshift([this.x, this.y]);
                
                this.vx *= FRICTION;
                this.vy *= FRICTION;
                this.vy += GRAVITY;
                
                this.x += this.vx;
                this.y += this.vy;
                this.alpha -= this.decay;

                if (this.alpha <= this.decay) {
                    particles.splice(index, 1);
                }
            }

            draw() {
                ctx!.beginPath();
                ctx!.moveTo(this.coordinates[this.coordinates.length - 1][0], this.coordinates[this.coordinates.length - 1][1]);
                ctx!.lineTo(this.x, this.y);
                ctx!.strokeStyle = `hsla(${this.hue}, 100%, ${this.brightness}%, ${this.alpha})`;
                ctx!.lineWidth = 1; // Thinner trail for particles
                ctx!.stroke();
            }
        }

        // --- State ---
        let fireworks: Firework[] = [];
        let particles: Particle[] = [];

        function createParticles(x: number, y: number, hue: number) {
            for (let i = 0; i < PARTICLE_COUNT; i++) {
                particles.push(new Particle(x, y, hue));
            }
        }

        let animationFrameId: number;

        const loop = () => {
            // Clear but keep transparency
            ctx!.clearRect(0, 0, w, h);
            
            // Randomly launch fireworks
            if (Math.random() < 0.08) { // Chance per frame
                 const startX = Math.random() * w;
                 // Restrict targetX to be closer to startX (straighter)
                 const spread = Math.min(w * 0.15, 200); 
                 let targetX = startX + random(-spread, spread);
                 if (targetX < 0) targetX = 0;
                 if (targetX > w) targetX = w;

                 const targetY = random(h * 0.1, h * 0.4); // Top half
                 fireworks.push(new Firework(startX, h, targetX, targetY));
            }

            // Update & Draw Fireworks
            let i = fireworks.length;
            while (i--) {
                fireworks[i].draw();
                fireworks[i].update(i);
            }

            // Update & Draw Particles
            let j = particles.length;
            while (j--) {
                particles[j].draw();
                particles[j].update(j);
            }

            animationFrameId = requestAnimationFrame(loop);
        };

        loop();

        return () => {
            window.removeEventListener('resize', resize);
            cancelAnimationFrame(animationFrameId);
        };
    }, []);

    return (
        <canvas 
            ref={canvasRef} 
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 9999,
                background: 'rgba(0, 0, 0, 0.7)', // Dark semi-transparent background
                cursor: 'pointer'
            }}
            onClick={(e) => {
                 const target = e.target as HTMLElement;
                 target.style.display = 'none';
            }}
        />
    );
};

export default VotePage;
